import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import entityComposition from '../../common/classes/entityComposition';
import FormEditor from '../../forms/formEditor';
import routeHelper from '../../common/classes/routeHelper';
import { viewPermissionSelector } from '../../common/classes/permissions';
import notificationConstants from '../../../constants/notificationConstants';
import useLookup from '../../../utils/useLookup';
const JobEditorContainer = _ref => {
  let canView = _ref.canView,
    location = _ref.location,
    _ref$job = _ref.job,
    job = _ref$job === void 0 ? {} : _ref$job,
    _ref$jobActions = _ref.jobActions,
    jobActions = _ref$jobActions === void 0 ? {} : _ref$jobActions,
    notificationActions = _ref.notificationActions,
    _ref$match$params = _ref.match.params,
    id = _ref$match$params.id,
    formID = _ref$match$params.formID;
  const active = job.active,
    _job$lastCanceledID = job.lastCanceledID,
    lastCanceledID = _job$lastCanceledID === void 0 ? null : _job$lastCanceledID,
    loading = job.loading,
    pendingChanges = job.pendingChanges;
  const serverSave = jobActions.serverSave,
    serverShow = jobActions.serverShow,
    showCancel = jobActions.showCancel;
  const pcLabel = useLookup('Phase Code').toLowerCase();
  const pcLabelPlural = useLookup('Phase Codes').toLowerCase();

  // Redirect and post authorization error message if permissions insufficient
  useEffect(() => {
    routeHelper.handleViewRedirect(canView, location);
  }, [canView, location]);

  // define an instance variable so that useCallback methods have the latest data
  const activeRef = useRef();
  useEffect(() => {
    activeRef.current = active;
    // reload the data if the URL id doesn't match the active record. Skip loading if
    // the job edit was just canceled via the transition popup
    if (id !== active.id && id !== lastCanceledID) {
      // load job by id into redux state for legacy components to consume
      serverShow(id);
    }
  }, [active, id, serverShow, lastCanceledID]);
  const validate = useCallback(() => {
    const errors = [];
    const current = activeRef.current;
    const jobNumber = current.jobNumber,
      assets = current.assets,
      phaseCodes = current.phaseCodes,
      piecesOther = current.piecesOther;
    if (!jobNumber) {
      errors.push('The job number is required.');
    } else if (!/^[a-zA-Z0-9 .()-]+$/.test(jobNumber)) {
      errors.push('Job numbers can only contain letters, numbers, ' + 'spaces, dashes, parentheses and periods.');
    }
    if ('assets' in current) {
      const noAsset = assets.filter(asset => typeof asset.assetID === 'undefined' || asset.assetID === null);
      if (noAsset.length) {
        errors.push('All of the job\'s associated assets ' + 'require an asset to be selected.');
      }
    }
    if ('phaseCodes' in current) {
      const noPhaseCodeName = phaseCodes.filter(phaseCode => !phaseCode.name);
      if (noPhaseCodeName.length) {
        errors.push("All of the job's ".concat(pcLabelPlural, " require a ").concat(pcLabel, " name."));
      }
    }
    if ('piecesOther' in current) {
      if (Array.isArray(piecesOther)) {
        const nameIsMissing = piecesOther.some(pieceOther => {
          let result = false;
          if (!pieceOther.name || pieceOther.name.length === 0) {
            result = true;
          }
          return result;
        });
        if (nameIsMissing) {
          errors.push('Piece name must not be blank.');
        }
      }
    }
    return errors;
  }, [activeRef, pcLabel, pcLabelPlural]);
  const handleBack = () => {
    routeHelper.updateURL('/admin/jobs');
  };
  const dismiss = notificationActions.dismiss,
    validationError = notificationActions.validationError;
  // useCallback prevents recreating this method, which is passed to children and will cause
  // re-rendering. To get the latest active record use a ref as recommended in the
  // docs: https://reactjs.org/docs/hooks-faq.html#how-to-read-an-often-changing-value-from-usecallback
  const handleSave = useCallback(async () => {
    const current = activeRef.current;
    const errors = validate();
    if (errors.length > 0) {
      validationError({
        list: errors,
        id: notificationConstants.VALIDATION_ERROR
      }, {
        autoClose: 10000
      });
      return null;
    }
    dismiss(notificationConstants.VALIDATION_ERROR); // remove validation-error notification

    const result = await serverSave(current);
    return result;
  }, [activeRef, dismiss, serverSave, validate, validationError]);
  const related = {
    loading,
    onBack: handleBack,
    onCancel: showCancel,
    onSave: handleSave,
    onValidate: validate,
    pendingChanges
  };
  return /*#__PURE__*/React.createElement(FormEditor, {
    id: formID,
    formTitle: active.jobNumber,
    related: related,
    sectionsLabel: "Job Sections",
    hideBackButton: false
  });
};
JobEditorContainer.propTypes = {
  canView: PropTypes.bool.isRequired,
  job: PropTypes.shape({
    active: PropTypes.shape({
      id: PropTypes.string,
      assets: PropTypes.arrayOf(PropTypes.shape({})),
      jobNumber: PropTypes.string,
      documents: PropTypes.arrayOf(PropTypes.shape({})),
      phaseCodes: PropTypes.arrayOf(PropTypes.shape({})),
      piecesOther: PropTypes.arrayOf(PropTypes.shape({})),
      flangeValveInspection: PropTypes.arrayOf(PropTypes.shape({})),
      flangeValve: PropTypes.arrayOf(PropTypes.shape({}))
    }),
    lastCanceledID: PropTypes.string,
    loading: PropTypes.bool.isRequired,
    pendingChanges: PropTypes.bool.isRequired
  }),
  jobActions: PropTypes.shape({
    serverSave: PropTypes.func,
    serverShow: PropTypes.func,
    showCancel: PropTypes.func
  }),
  location: PropTypes.shape({}).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
      formID: PropTypes.string
    }),
    url: PropTypes.string
  }).isRequired,
  notificationActions: PropTypes.shape({
    validationError: PropTypes.func,
    dismiss: PropTypes.func
  }).isRequired
};
export { JobEditorContainer as TestJobEditorContainer };
export default entityComposition(JobEditorContainer, ['job'], {
  canView: state => viewPermissionSelector('job')(state)
}, ['notification']);